import React, { useEffect, useState } from "react";
import axios from "axios";
import { Download, Search } from "lucide-react";
import { toast } from "sonner";

const InvoiceReportPage = () => {
  const [category, setCategory] = useState("");
  const [dateMode, setDateMode] = useState("range");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [month, setMonth] = useState(String(new Date().getMonth() + 1).padStart(2, "0"));
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [rows, setRows] = useState([]);
  const [previewReady, setPreviewReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const API_URL = "/api";

  const getDates = () => {
    if (dateMode === "month") {
      const lastDay = new Date(Number(year), Number(month), 0).getDate();
      return {
        from: `${year}-${month}-01`,
        to: `${year}-${month}-${String(lastDay).padStart(2, "0")}`,
      };
    }
    return { from: dateFrom, to: dateTo };
  };

  const loadPreview = async () => {
    const dates = getDates();
    if (dates.from && dates.to && dates.from > dates.to) {
      toast.error("Tanggal awal tidak boleh melewati tanggal akhir");
      return;
    }
    if (!dates.from || !dates.to) {
      toast.error("Pilih rentang tanggal atau bulan dan tahun dulu");
      return;
    }
    setPreviewReady(false);
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category) params.set("category", category);
      if (dates.from) params.set("date_from", dates.from);
      if (dates.to) params.set("date_to", dates.to);
      const res = await axios.get(`${API_URL}/reports/myob/preview?${params.toString()}`);
      setRows(res.data.data || []);
      setPreviewReady(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal memuat preview report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPreviewReady(false);
    setRows([]);
  }, [category, dateMode, dateFrom, dateTo, month, year]);

  const handleExport = async () => {
    if (!previewReady || !rows.length) return toast.error("Tampilkan preview terlebih dahulu");
    setExporting(true);
    const loadingToast = toast.loading("Membentuk file MYOB...");
    try {
      const res = await axios.post(
        `${API_URL}/invoices/history/export-myob`,
        { ids: rows.flatMap((row) => row.ids) },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "MYOB_Export_Report.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("MYOB berhasil didownload", { id: loadingToast });
    } catch (error) {
      toast.error("Gagal mendownload MYOB", { id: loadingToast });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Report</h1>
        <p className="text-sm text-gray-500 mt-1">Preview dan export invoice ke format MYOB.</p>
      </div>

      <div className="bg-white rounded-2xl border p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <label className="text-sm font-medium">
            Kategori
            <select className="mt-1 w-full border rounded-lg p-2.5" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Semua (C, D, K)</option>
              <option value="EXPORT">Export (C)</option>
              <option value="DOMESTIC">Domestic (D)</option>
              <option value="IMPORT">Import (K)</option>
            </select>
          </label>
          <label className="text-sm font-medium">
            Jenis filter tanggal
            <select className="mt-1 w-full border rounded-lg p-2.5" value={dateMode} onChange={(e) => setDateMode(e.target.value)}>
              <option value="range">Rentang tanggal</option>
              <option value="month">Bulan dan tahun</option>
            </select>
          </label>
          {dateMode === "range" ? (
            <>
              <label className="text-sm font-medium">
                From
                <input type="date" className="mt-1 w-full border rounded-lg p-2.5" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </label>
              <label className="text-sm font-medium">
                Until
                <input type="date" className="mt-1 w-full border rounded-lg p-2.5" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </label>
            </>
          ) : (
            <>
              <label className="text-sm font-medium">
                Bulan
                <select className="mt-1 w-full border rounded-lg p-2.5" value={month} onChange={(e) => setMonth(e.target.value)}>
                  {Array.from({ length: 12 }, (_, i) => {
                    const value = String(i + 1).padStart(2, "0");
                    return <option key={value} value={value}>{new Date(2000, i, 1).toLocaleString("id-ID", { month: "long" })}</option>;
                  })}
                </select>
              </label>
              <label className="text-sm font-medium">
                Tahun
                <input type="number" className="mt-1 w-full border rounded-lg p-2.5" min="2000" max="2100" value={year} onChange={(e) => setYear(e.target.value)} />
              </label>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={loadPreview} disabled={loading} className="px-4 py-2.5 rounded-lg bg-blue-600 text-white font-semibold flex items-center gap-2 disabled:opacity-50">
            <Search size={16} /> {loading ? "Memuat..." : "Tampilkan Preview"}
          </button>
          <button onClick={handleExport} disabled={exporting || loading || !previewReady || rows.length === 0} className="px-4 py-2.5 rounded-lg bg-purple-700 text-white font-semibold flex items-center gap-2 disabled:opacity-50">
            <Download size={16} /> {exporting ? "Membuat file..." : "Export MYOB"}
          </button>
          <span className="text-sm text-gray-600">{rows.length.toLocaleString("id-ID")} invoice</span>
        </div>
      </div>

      <div className="bg-white rounded-2xl border overflow-hidden">
        <div className="max-h-[760px] overflow-auto">
          <table className="min-w-max w-full text-sm whitespace-nowrap">
            <thead className="sticky top-0 z-10 bg-gray-50 text-gray-600 shadow-sm">
              <tr>
                <th className="text-left p-3">Invoice #</th>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Customer PO:</th>
                <th className="text-left p-3">Description</th>
                <th className="text-left p-3">Account #</th>
                <th className="text-right p-3">Amount</th>
                <th className="text-right p-3">Inc Tax Amnt</th>
                <th className="text-left p-3">Journal Memo</th>
                <th className="text-left p-3">Tax Code</th>
                <th className="text-right p-3">Non-GST Amount</th>
                <th className="text-right p-3">Freight Amount</th>
                <th className="text-left p-3">Freight Tax Code</th>
                <th className="text-left p-3">Currency Code</th>
                <th className="text-right p-3">Exchange Rate</th>
                <th className="text-left p-3">Card ID</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="15" className="p-8 text-center text-gray-500">Memuat data...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="15" className="p-8 text-center text-gray-500">Tidak ada data untuk filter ini.</td></tr>
              ) : rows.map((row) => (
                <tr key={row.invoice_number} className="border-t hover:bg-gray-50">
                  <td className="p-3">{row.invoice_clean}</td>
                  <td className="p-3">{row.invoice_date ? new Date(row.invoice_date).toLocaleDateString("en-US") : ""}</td>
                  <td className="p-3">{row.invoice_number}</td>
                  <td className="p-3">{row.description}</td>
                  <td className="p-3">{row.account}</td>
                  <td className="p-3 text-right">{row.amount}</td>
                  <td className="p-3 text-right">{row.inc_tax_amount}</td>
                  <td className="p-3">{row.journal_memo}</td>
                  <td className="p-3">GST</td>
                  <td className="p-3 text-right">{row.vat_amount}</td>
                  <td className="p-3 text-right">0</td>
                  <td className="p-3">N-T</td>
                  <td className="p-3">IDR</td>
                  <td className="p-3 text-right">1</td>
                  <td className="p-3">{row.card_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InvoiceReportPage;
